import { Sidebar, SidebarHeader, close, toggle } from '@/features/sidebar';
import { query } from '@/services/api/services-api';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { AccountState } from '@/services/auth/types';
import { useSupportChat } from '@/services/support-chat';
import { isEmptyString } from '@/utils/string';
import {
  Box,
  Button,
  Divider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Heading,
  Stack,
} from '@customerio/pluma-components/react';
import { ResourceLink } from './components/resource-link';
import { SystemStatus } from './components/system-status';
import * as styles from './index.css';

const SIDEBAR_ID = 'help-dropdown';

export function useWorkrampAuth() {
  const auth = useAuth();
  const mutation = query.getWorkrampAuthUrl.useMutation();

  const handleWorkrampAuth = async (dest: string, fallbackUrl: string) => {
    try {
      const accountId = auth.account?.id;
      const userId = auth.user?.id;

      if (accountId == null || userId == null) {
        window.open(fallbackUrl, '_blank');
        return;
      }

      const response = await mutation.mutateAsync({
        params: { accountId, userId },
        body: { dest },
      });

      if (response.status === 200 && !isEmptyString(response.body.url)) {
        window.open(response.body.url, '_blank');
      } else {
        window.open(fallbackUrl, '_blank');
      }
    } catch (_error) {
      // Fall back to static URL if the API call fails
      window.open(fallbackUrl, '_blank');
    }
  };

  return { handleWorkrampAuth };
}

export function HelpSidebar() {
  const handleClose = () => {
    void close();
  };

  const auth = useAuth();
  const aiEnabled = useFeatureFlag(AccountFeatureFlag.AiEnabled);
  const { isHandoverAllowed, openChat } = useSupportChat();
  const { handleWorkrampAuth } = useWorkrampAuth();
  const showCommunity = auth.account?.account_state === AccountState.Current;

  return (
    <Sidebar id={SIDEBAR_ID}>
      <div className={styles.container}>
        <SidebarHeader title="Help & Support" onClose={handleClose} />
        <div className={styles.content}>
          <Stack gap="200">
            <Heading level="6">Resources</Heading>
            <Stack>
              <ResourceLink
                title="Customer.io Documentation"
                icon="plaintext"
                href="https://docs.customer.io/"
                isExternal
              />
              <ResourceLink
                title="API Documentation"
                icon="transactional-api"
                href="https://docs.customer.io/integrations/api/customerio-apis/"
                isExternal
              />
              {showCommunity ? (
                <ResourceLink
                  title="Ask the Community"
                  icon="social"
                  onClick={async () => {
                    await handleWorkrampAuth('community', 'https://academy.customer.io/community');
                  }}
                />
              ) : null}
              <ResourceLink
                title="Release Notes"
                icon="feature-launch"
                href="https://docs.customer.io/release-notes/"
                isExternal
              />
              <Divider my="200" />
              <ResourceLink
                title="Join the Customer.io Academy"
                icon="winner"
                description="Get certified and become a Customer.io expert."
                onClick={async () => {
                  await handleWorkrampAuth('academy', 'https://academy.customer.io/academy');
                }}
              />
              <ResourceLink
                title="Explore a sample workspace"
                icon="workspaces"
                description="Pretend data and view only"
                href="/sample-workspace"
                onClick={() => {
                  void close();
                }}
              />
              <Divider my="200" />
              <SystemStatus />
            </Stack>
          </Stack>
          {/* In-app messaging target for Customer.io messages */}
          <div id="in-app-target_help-center" />
        </div>
        <div className={styles.footer}>
          <Box display="flex" gap="100" justifyContent="flex-end">
            <Button variant="secondary" href="/settings/privacy?expandAccessControls=true">
              Grant support access
            </Button>
            <DropdownMenu placement="top-end">
              <DropdownMenuTriggerButton variant="primary" icon="chat">
                Get Help
              </DropdownMenuTriggerButton>
              <DropdownMenuContent>
                {aiEnabled ? (
                  <DropdownMenuItem
                    icon="chat"
                    description="Fastest way to get help"
                    onClick={() => {
                      toggle('copilot');
                    }}
                  >
                    Chat with AI Assistant
                  </DropdownMenuItem>
                ) : null}
                {isHandoverAllowed ? (
                  <DropdownMenuItem
                    icon="chat-question"
                    description="Monday - Friday 5:00 am - 8:00 pm EST"
                    onClick={() => {
                      void openChat();
                    }}
                  >
                    Chat with support
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  icon="help"
                  description="Expect a response in 24hrs or less"
                  onClick={() => {
                    // TODO: Implement support request submission
                  }}
                >
                  Submit a support request
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Box>
        </div>
      </div>
    </Sidebar>
  );
}
